import type { VoidFunctionComponent, ChangeEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import styles from './styles.module.less';

export type Step = {
  label: string;
  value: string;
};

type RangeSliderProps = {
  steps: Step[];
  onChange: (value: string) => void;
  defaultStepValue: string,
};

const RangeSlider: VoidFunctionComponent<RangeSliderProps> = ({
  steps,
  onChange,
  defaultStepValue,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const handleSliderChange = (event: ChangeEvent<HTMLInputElement>) => {
    const stepIndex = Number(event.target.value);
    updateSlider(stepIndex);
  };

  const handleSelectStep = (stepIndex: number) => {
    updateSlider(stepIndex);
  };

  const updateSlider = useCallback((stepIndex: number) => {
    setCurrentStep(stepIndex);

    // Find the selected step object
    const selectedStep = steps[stepIndex];
    if (selectedStep) {
      onChange(selectedStep.value);
    }
  }, [onChange, setCurrentStep, steps]);

  // Set the percentage for filling the progress bar
  const fillPercentage = (currentStep / (steps.length - 1)) * 100;

  useEffect(() => {
    if (defaultStepValue) {
      const stepIndex = steps.findIndex((step) => step.value === defaultStepValue);
      updateSlider(stepIndex);
    }
  }, [steps, defaultStepValue, updateSlider]);

  return (
    <div className={styles.rangeSlider}>
      <div
        className={styles.rangeSliderTrack}
        style={{ backgroundSize: `${fillPercentage}% 100%` }}
      />

      <input
        type="range"
        min="0"
        max={steps.length - 1}
        step="1"
        value={currentStep}
        onChange={handleSliderChange}
        className={styles.rangeSliderInput}
      />

      <div className={styles.rangeSliderSteps}>
        {steps.map((step, index) => (
          <button
            type="button"
            key={step.value}
            onClick={() => handleSelectStep(index)}
            className={index === currentStep ? styles.active : ''}
          >
            {step.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default RangeSlider;
